import React, { useState } from 'react';
import { SEO } from '@/components/common/SEO';
import SleekButton from '@/components/ui/NavButtons';
import { track } from '@/utils/analytics';

const CHECK_ITEMS = [
  "Walk through WealthScope and PortfolioIQ with your actual use case",
  "See how DSCR and Schedule E reports are generated in one click",
  "Get answers to your specific investing questions",
  "No commitment — just an honest conversation",
];

const TRUST = [
  { label: "14-day free trial", sub: "We'll remind you before you're charged" },
  { label: "Cancel anytime", sub: "No long-term contracts" },
  { label: "Bank-level security", sub: "SOC 2 Type II audit underway" },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEMO_WEBHOOK = (import.meta as any).env?.VITE_DEMO_WEBHOOK_URL as string | undefined;

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    if (DEMO_WEBHOOK) {
      try {
        await fetch(DEMO_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, source: 'contact-page' }),
        });
      } catch {
        // Fail silently — still show success to user
      }
    }
    setSubmitting(false);
    setSubmitted(true);
    track.demoRequested();
  };

  return (
    <>
      <SEO
        title="Contact VaWeNa — Book a Free Demo"
        description="Talk to our team and see how WealthScope and PortfolioIQ can transform your real estate investing. Book a free 30-minute demo."
        canonical="/contact"
      />
      <div className="w-full min-h-screen dark:bg-[#020D30] bg-[#f0f4ff]">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">

          {/* Page header */}
          <div className="text-center mb-14">
            <div className="inline-flex px-4 py-1.5 text-xs font-semibold tracking-widest text-blue-500 uppercase bg-blue-500/10 rounded-full mb-4">
              Talk to Us
            </div>
            <h1 className="text-3xl md:text-4xl font-bold dark:text-white text-gray-900 mb-3">
              See VaWeNa in action
            </h1>
            <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto">
              Book a free 30-minute demo and we'll walk through your specific portfolio and use case.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">

            {/* LEFT — What you get */}
            <div>
              <h2 className="text-xl font-semibold dark:text-white text-gray-800 mb-6">
                What's in the demo
              </h2>
              <ul className="space-y-4 mb-10">
                {CHECK_ITEMS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600/20 flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                      </svg>
                    </span>
                    <span className="text-sm dark:text-slate-300 text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {TRUST.map((t, i) => (
                  <div key={i} className="dark:bg-white/5 bg-white border dark:border-white/10 border-gray-200 rounded-xl px-4 py-4">
                    <div className="text-sm font-semibold dark:text-white text-gray-800">{t.label}</div>
                    <div className="text-xs dark:text-slate-400 text-gray-500 mt-0.5">{t.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — Contact form */}
            <div className="dark:bg-vw-surface bg-white border dark:border-white/10 border-gray-200 rounded-2xl p-8 shadow-sm">
              {submitted ? (
                <div className="flex flex-col items-center text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-blue-600/20 flex items-center justify-center mb-4">
                    <svg className="w-7 h-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold dark:text-white text-gray-800 mb-2">Thanks, {form.name || 'there'}!</h3>
                  <p className="text-sm dark:text-slate-400 text-gray-500">
                    We'll reach out within one business day to schedule your demo.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold dark:text-white text-gray-800 mb-6">Book your free demo</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium dark:text-slate-300 text-gray-600 mb-1.5">
                          Full name <span className="text-blue-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Jane Smith"
                          className="w-full px-3 py-2.5 text-sm rounded-lg dark:bg-white/5 bg-gray-50 dark:border-white/10 border-gray-200 border dark:text-white text-gray-800 dark:placeholder-slate-500 placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 focus:ring-2 focus:ring-blue-500/50 transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium dark:text-slate-300 text-gray-600 mb-1.5">
                          Work email <span className="text-blue-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="jane@example.com"
                          className="w-full px-3 py-2.5 text-sm rounded-lg dark:bg-white/5 bg-gray-50 dark:border-white/10 border-gray-200 border dark:text-white text-gray-800 dark:placeholder-slate-500 placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 focus:ring-2 focus:ring-blue-500/50 transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium dark:text-slate-300 text-gray-600 mb-1.5">
                        Company / firm (optional)
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Acme Investments"
                        className="w-full px-3 py-2.5 text-sm rounded-lg dark:bg-white/5 bg-gray-50 dark:border-white/10 border-gray-200 border dark:text-white text-gray-800 dark:placeholder-slate-500 placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 focus:ring-2 focus:ring-blue-500/50 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium dark:text-slate-300 text-gray-600 mb-1.5">
                        What would you like to learn? (optional)
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="e.g. I have 5 rental properties and want to track DSCR and generate Schedule E reports..."
                        className="w-full px-3 py-2.5 text-sm rounded-lg dark:bg-white/5 bg-gray-50 dark:border-white/10 border-gray-200 border dark:text-white text-gray-800 dark:placeholder-slate-500 placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 focus:ring-2 focus:ring-blue-500/50 transition resize-none"
                      />
                    </div>

                    <SleekButton variant="primary" type="submit" className="w-full justify-center mt-2" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Book Free Demo'}
                    </SleekButton>

                    <p className="text-xs dark:text-slate-500 text-gray-400 text-center">
                      No commitment. We'll respond within one business day.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};
